import { EventsApiService } from '@adminServices';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationService } from '@shared';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as EventsActions from './events.actions';

@Injectable()
export class EventsEffects {
  private actions$ = inject(Actions);
  private eventsApi = inject(EventsApiService);
  private notification = inject(NotificationService);

  loadEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.loadEvents),
      switchMap(({ params }) =>
        this.eventsApi.findAll(params).pipe(
          map(response => EventsActions.loadEventsSuccess({ response })),
          catchError((error) => {
            this.notification.error('admin.notifications.load_events_error');
            return of(EventsActions.loadEventsFailure({ error: error.message || 'Failed to load events' }));
          }),
        ),
      ),
    ),
  );

  loadEventById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.loadEventById),
      switchMap(({ id }) =>
        this.eventsApi.findOne(id).pipe(
          map(event => EventsActions.loadEventByIdSuccess({ event })),
          catchError((error) => {
            this.notification.error('admin.notifications.load_event_error');
            return of(EventsActions.loadEventByIdFailure({ error: error.message || 'Failed to load event' }));
          }),
        ),
      ),
    ),
  );

  createEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.createEvent),
      switchMap(({ eventData }) =>
        this.eventsApi.create(eventData).pipe(
          map(event => {
            this.notification.success('admin.notifications.event_created');
            return EventsActions.createEventSuccess({ event });
          }),
          catchError((error) => {
            this.notification.error('admin.notifications.create_event_error');
            return of(EventsActions.createEventFailure({ error: error.message || 'Failed to create event' }));
          }),
        ),
      ),
    ),
  );

  updateEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.updateEvent),
      switchMap(({ id, eventData }) =>
        this.eventsApi.update(id, eventData).pipe(
          map(event => {
            this.notification.success('admin.notifications.event_updated');
            return EventsActions.updateEventSuccess({ event });
          }),
          catchError((error) => {
            this.notification.error('admin.notifications.update_event_error');
            return of(EventsActions.updateEventFailure({ error: error.message || 'Failed to update event' }));
          }),
        ),
      ),
    ),
  );

  deleteEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.deleteEvent),
      switchMap(({ id }) => {
        const deleteImages$ = forkJoin([
          this.eventsApi.deleteTitleImage(id).pipe(catchError(() => of(null))),
          this.eventsApi.deleteThumbnail(id).pipe(catchError(() => of(null))),
        ]);

        return deleteImages$.pipe(
          switchMap(() =>
            this.eventsApi.delete(id).pipe(
              map(() => {
                this.notification.success('admin.notifications.event_deleted');
                return EventsActions.deleteEventSuccess({ id });
              }),
              catchError((error) => {
                this.notification.error('admin.notifications.delete_event_error');
                return of(EventsActions.deleteEventFailure({ error: error.message || 'Failed to delete event' }));
              }),
            ),
          ),
        );
      }),
    ),
  );

  uploadTitleImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.uploadTitleImage),
      switchMap(({ id, file }) =>
        this.eventsApi.uploadTitleImage(id, file).pipe(
          map(event => {
            this.notification.success('admin.notifications.image_uploaded');
            return EventsActions.uploadTitleImageSuccess({ event });
          }),
          catchError((error) => {
            this.notification.error('admin.notifications.upload_image_error');
            return of(EventsActions.uploadTitleImageFailure({ error: error.message || 'Failed to upload image' }));
          }),
        ),
      ),
    ),
  );

  uploadThumbnail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.uploadThumbnail),
      switchMap(({ id, file }) =>
        this.eventsApi.uploadThumbnail(id, file).pipe(
          map(event => {
            this.notification.success('admin.notifications.image_uploaded');
            return EventsActions.uploadThumbnailSuccess({ event });
          }),
          catchError((error) => {
            this.notification.error('admin.notifications.upload_image_error');
            return of(EventsActions.uploadThumbnailFailure({ error: error.message || 'Failed to upload thumbnail' }));
          }),
        ),
      ),
    ),
  );

  deleteTitleImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.deleteTitleImage),
      switchMap(({ id }) =>
        this.eventsApi.deleteTitleImage(id).pipe(
          map(event => {
            this.notification.success('admin.notifications.image_deleted');
            return EventsActions.deleteTitleImageSuccess({ event });
          }),
          catchError((error) => {
            this.notification.error('admin.notifications.delete_image_error');
            return of(EventsActions.deleteTitleImageFailure({ error: error.message || 'Failed to delete image' }));
          }),
        ),
      ),
    ),
  );

  deleteThumbnail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.deleteThumbnail),
      switchMap(({ id }) =>
        this.eventsApi.deleteThumbnail(id).pipe(
          map(event => {
            this.notification.success('admin.notifications.image_deleted');
            return EventsActions.deleteThumbnailSuccess({ event });
          }),
          catchError((error) => {
            this.notification.error('admin.notifications.delete_image_error');
            return of(EventsActions.deleteThumbnailFailure({ error: error.message || 'Failed to delete thumbnail' }));
          }),
        ),
      ),
    ),
  );

}

